import React, { useEffect, useMemo, useState } from 'react';
import type {
  LessonStepPathway,
  RoutePersistence,
  RouteStudentsToStepPayload,
  RouteStudentsToStepResponse,
} from '../../../types/v2';

interface RouteablePathwayStep {
  id: number;
  order: number;
  title: string;
  stage_tag?: string | null;
  runner_phase?: 'live' | 'studio';
  step_kind?: string;
  content?: Record<string, any> | null;
}

interface OpsAdaptivePanelProps {
  showReviewed: boolean;
  adaptiveLoading: boolean;
  adaptiveError: string | null;
  adaptiveSummary: any;
  adaptiveSuggestions: any[];
  expandedAdaptiveTargets: Set<number>;
  rosterNameById: Map<number, string>;
  selectedStudentIds: number[];
  routeablePathwaySteps: RouteablePathwayStep[];
  lastAdaptiveApplication: { repair: number; extend: number } | null;
  computeMinutesUntil: (iso?: string | null) => number | null;
  onRouteStudentsToStep: (payload: RouteStudentsToStepPayload) => Promise<RouteStudentsToStepResponse>;
  onToggleShowReviewed: (nextValue: boolean) => void;
  onGenerateAdaptive: () => void;
  onClearOverridesToClassStep: () => void;
  onToggleAdaptiveTargets: (suggestionId: number) => void;
  onApproveApplySuggestion: (suggestionId: number, apply: boolean) => void;
  onRejectSuggestion: (suggestionId: number) => void;
  onApplyApprovedSuggestion: (suggestionId: number) => void;
}

export const OpsAdaptivePanel: React.FC<OpsAdaptivePanelProps> = ({
  showReviewed,
  adaptiveLoading,
  adaptiveError,
  adaptiveSummary,
  adaptiveSuggestions,
  expandedAdaptiveTargets,
  rosterNameById,
  selectedStudentIds,
  routeablePathwaySteps,
  lastAdaptiveApplication,
  computeMinutesUntil,
  onRouteStudentsToStep,
  onToggleShowReviewed,
  onGenerateAdaptive,
  onClearOverridesToClassStep,
  onToggleAdaptiveTargets,
  onApproveApplySuggestion,
  onRejectSuggestion,
  onApplyApprovedSuggestion,
}) => {
  const [selectedStepId, setSelectedStepId] = useState<number | ''>('');
  const [persistence, setPersistence] = useState<RoutePersistence>('until_class_advance');
  const [routingLoading, setRoutingLoading] = useState(false);
  const [routingError, setRoutingError] = useState<string | null>(null);
  const [routingSuccess, setRoutingSuccess] = useState<string | null>(null);

  const selectedStep = useMemo(() => (
    routeablePathwaySteps.find((step) => Number(step.id) === Number(selectedStepId)) || null
  ), [routeablePathwaySteps, selectedStepId]);

  const selectedPathway = useMemo<LessonStepPathway | null>(() => {
    const pathway = selectedStep?.content?.pathway;
    return pathway && typeof pathway === 'object' && !Array.isArray(pathway) ? pathway as LessonStepPathway : null;
  }, [selectedStep]);

  const selectedRouteType = String(selectedPathway?.type || 'core').trim().toLowerCase() || 'core';
  const defaultPersistenceForStep: RoutePersistence = ['support', 'extension', 'activation'].includes(selectedRouteType)
    ? 'until_cleared'
    : 'until_class_advance';

  useEffect(() => {
    if (routeablePathwaySteps.length === 0) {
      setSelectedStepId('');
      return;
    }
    setSelectedStepId((current) => (
      routeablePathwaySteps.some((step) => Number(step.id) === Number(current))
        ? current
        : routeablePathwaySteps[0].id
    ));
  }, [routeablePathwaySteps]);

  useEffect(() => {
    setPersistence(defaultPersistenceForStep);
  }, [defaultPersistenceForStep, selectedStepId]);

  const selectedStudentPreview = selectedStudentIds.slice(0, 8);
  const remainingSelectedCount = Math.max(0, selectedStudentIds.length - selectedStudentPreview.length);
  const canRoute = selectedStudentIds.length > 0 && Boolean(selectedStepId) && routeablePathwaySteps.length > 0 && !routingLoading;

  const formatPathwayType = (type?: string | null): string => {
    const key = String(type || '').trim().toLowerCase();
    if (key === 'support') return 'Support First';
    if (key === 'extension') return 'Challenge';
    if (key === 'activation') return 'Start Here';
    if (key === 'teacher_table') return 'Teacher Table';
    if (key === 'core') return 'Core';
    return key ? key.replace(/_/g, ' ') : 'Core';
  };

  const pathwayForStep = (step: RouteablePathwayStep | null | undefined): LessonStepPathway | null => {
    const pathway = step?.content?.pathway;
    return pathway && typeof pathway === 'object' && !Array.isArray(pathway) ? pathway as LessonStepPathway : null;
  };

  const formatStepLabel = (step: RouteablePathwayStep): string => {
    const pathway = pathwayForStep(step);
    const label = String(pathway?.route_label || step.title || `Step ${Number(step.order || 0) + 1}`).trim();
    const minutes = Number(pathway?.estimated_minutes || 0);
    const details = [
      formatPathwayType(pathway?.type),
      pathway?.route_only === true ? 'hidden until routed' : 'visible in Studio',
      Number.isFinite(minutes) && minutes > 0 ? `${minutes} min` : '',
    ].filter(Boolean).join(', ');
    const title = step.title && step.title !== label ? ` - ${step.title}` : '';
    return details ? `${label}${title} (${details})` : `${label}${title}`;
  };

  const selectedMinutes = Number(selectedPathway?.estimated_minutes || 0);

  const handleRouteSelected = async () => {
    if (!canRoute || !selectedStepId) return;
    setRoutingLoading(true);
    setRoutingError(null);
    setRoutingSuccess(null);
    try {
      const response = await onRouteStudentsToStep({
        student_ids: selectedStudentIds,
        step_id: Number(selectedStepId),
        reason: selectedRouteType || 'core',
        persistence,
      });
      const routedCount = Array.isArray(response?.routed_student_ids) ? response.routed_student_ids.length : selectedStudentIds.length;
      setRoutingSuccess(`Sent ${routedCount} student${routedCount === 1 ? '' : 's'} to ${selectedPathway?.route_label || selectedStep?.title || 'the selected step'}.`);
    } catch (err: any) {
      setRoutingError(err?.response?.data?.error || err?.message || 'Failed to route selected students.');
    } finally {
      setRoutingLoading(false);
    }
  };

  return (
  <div className="bg-white rounded-lg border border-gray-200 p-4">
    <div className="flex items-center justify-between">
      <div>
        <div className="font-semibold text-sm text-gray-900">Pathway Reroute</div>
        <div className="mt-0.5 text-xs text-gray-500">
          Use after Studio has started to send selected students to support, challenge, or activation steps.
        </div>
      </div>
      <div className="flex items-center gap-3">
        <label className="inline-flex items-center gap-1 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={showReviewed}
            onChange={(e) => onToggleShowReviewed(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          <span>Show reviewed</span>
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            className="px-3 py-1 text-sm bg-indigo-600 text-white rounded disabled:opacity-60"
            onClick={onGenerateAdaptive}
            disabled={adaptiveLoading}
          >
            {adaptiveLoading ? 'Checking…' : 'Check Adaptive'}
          </button>
          <button
            type="button"
            className="px-3 py-1 text-sm border border-emerald-300 bg-emerald-50 text-emerald-800 rounded disabled:opacity-60"
            onClick={onClearOverridesToClassStep}
            disabled={adaptiveLoading}
            title="Return routed students to the current class step"
          >
            Return Routed Students
          </button>
        </div>
      </div>
    </div>

    {adaptiveError ? (
      <div className="mt-2 text-sm text-red-600">{adaptiveError}</div>
    ) : null}

    <div className="mt-2 text-sm text-gray-700">
      {adaptiveSummary ? (
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          <span>Aligned: <span className="font-semibold">{adaptiveSummary.aligned ?? 0}</span></span>
          <span>Intervention: <span className="font-semibold">{adaptiveSummary.intervention ?? 0}</span></span>
          <span>Acceleration: <span className="font-semibold">{adaptiveSummary.acceleration ?? 0}</span></span>
          <span>Unknown: <span className="font-semibold">{adaptiveSummary.unknown ?? 0}</span></span>
        </div>
      ) : (
        <div className="text-gray-500">No adaptive state loaded yet.</div>
      )}
    </div>

    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="font-semibold text-sm text-slate-900">Pathway Reroute</div>
          <div className="mt-1 text-xs text-slate-600">
            {selectedStudentIds.length > 0
              ? `${selectedStudentIds.length} selected`
              : 'Select students first, then send them to a pathway step.'}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            This does not move the class step. Persistent routes stay active until you return students.
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-fit rounded-full bg-sky-100 px-2.5 py-1 text-[11px] font-medium text-sky-800">
            {formatPathwayType(selectedPathway?.type)}
          </span>
          <span className="w-fit rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-medium text-amber-800">
            {persistence === 'until_cleared' ? 'Stay until cleared' : 'Until next class advance'}
          </span>
        </div>
      </div>

      {selectedStudentPreview.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {selectedStudentPreview.map((studentId) => (
            <span key={`manual-route-student-${studentId}`} className="rounded-full bg-white px-2 py-1 text-xs text-slate-700 ring-1 ring-slate-200">
              {rosterNameById.get(studentId) || `ID ${studentId}`}
            </span>
          ))}
          {remainingSelectedCount > 0 ? (
            <span className="px-1 py-1 text-xs text-slate-500">+{remainingSelectedCount} more</span>
          ) : null}
        </div>
      ) : null}

      {routeablePathwaySteps.length === 0 ? (
        <div className="mt-3 rounded border border-dashed border-slate-300 bg-white px-3 py-2 text-sm text-slate-500">
          No routeable pathway steps have been authored for this lesson.
        </div>
      ) : (
        <div className="mt-3 grid gap-2 lg:grid-cols-[1fr_220px_auto] lg:items-end">
          <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Pathway step
            <select
              value={selectedStepId}
              onChange={(event) => {
                setSelectedStepId(Number(event.target.value) || '');
                setRoutingError(null);
                setRoutingSuccess(null);
              }}
              className="mt-1 block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm normal-case tracking-normal text-slate-800"
            >
              {routeablePathwaySteps.map((step) => (
                <option key={`manual-route-step-${step.id}`} value={step.id}>
                  {formatStepLabel(step)}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Return behavior
            <select
              value={persistence}
              onChange={(event) => {
                setPersistence(event.target.value as RoutePersistence);
                setRoutingError(null);
                setRoutingSuccess(null);
              }}
              className="mt-1 block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm normal-case tracking-normal text-slate-800"
            >
              <option value="until_cleared">Stay until cleared</option>
              <option value="until_class_advance">Until next class advance</option>
            </select>
          </label>
          <button
            type="button"
            className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => { void handleRouteSelected(); }}
            disabled={!canRoute}
          >
            {routingLoading ? 'Sending...' : 'Send to Step'}
          </button>
        </div>
      )}

      {selectedStep ? (
        <div className="mt-3 rounded-lg border border-sky-200 bg-white px-3 py-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-900">
                {selectedPathway?.route_label || selectedStep.title || 'Selected pathway'}
              </div>
              <div className="mt-1 text-xs text-slate-600">
                {selectedStep.title || `Step ${Number(selectedStep.order || 0) + 1}`}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-800">
                {formatPathwayType(selectedPathway?.type)}
              </span>
              {selectedPathway?.route_only === true ? (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                  Hidden until routed
                </span>
              ) : null}
              {Number.isFinite(selectedMinutes) && selectedMinutes > 0 ? (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                  {selectedMinutes} min
                </span>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <div className="mt-3 flex flex-col gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-sm font-semibold text-emerald-900">Return routed students</div>
          <div className="mt-0.5 text-xs text-emerald-800">
            Clears active pathway routes and brings students back to the current class step.
          </div>
        </div>
        <button
          type="button"
          className="w-fit rounded border border-emerald-300 bg-white px-3 py-1.5 text-sm font-medium text-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
          onClick={onClearOverridesToClassStep}
          disabled={adaptiveLoading}
        >
          Return Routed Students
        </button>
      </div>

      {routingError ? (
        <div className="mt-2 text-sm text-red-600">{routingError}</div>
      ) : null}
      {routingSuccess ? (
        <div className="mt-2 text-sm text-emerald-700">{routingSuccess}</div>
      ) : null}
    </div>

    <div className="mt-3">
      <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Suggestions</div>
      {adaptiveSuggestions.length === 0 ? (
        <div className="text-sm text-gray-500">None.</div>
      ) : (
        <div className="w-full min-w-0 space-y-2">
          {adaptiveSuggestions.map((s: any) => {
            const suggestionId = Number(s.id);
            const statusText = String(s?.status || '').toLowerCase();
            const isPending = statusText === 'pending' || !statusText;
            const canApplyNow = statusText === 'approved' && !s?.applied_at;
            const mins = computeMinutesUntil(s?.expires_at);
            const targets = Array.isArray(s?.target_student_ids) ? s.target_student_ids.length : (s?.target_count ?? 0);
            const targetIds = Array.isArray(s?.target_student_ids) ? s.target_student_ids : [];
            const previewTargetIds = targetIds.slice(0, 10);
            const remainingTargetCount = Math.max(0, targetIds.length - previewTargetIds.length);
            const previewOpen = expandedAdaptiveTargets.has(suggestionId);
            const rationale = s?.rationale || {};
            const recommendation = s?.recommendation || {};
            const rawReasonCounts = recommendation?.reason_counts || rationale?.reason_counts || {};
            const reasonCounts = rawReasonCounts && typeof rawReasonCounts === 'object' ? rawReasonCounts : {};
            const reasonSummary = Object.entries(reasonCounts)
              .map(([key, value]) => {
                const count = Number(value ?? 0);
                if (!Number.isFinite(count) || count <= 0) return null;
                return `${count} ${String(key).replace(/_/g, ' ')}`;
              })
              .filter(Boolean)
              .slice(0, 4)
              .join(' • ');
            const teacherMovesRaw = Array.isArray(recommendation?.teacher_moves)
              ? recommendation.teacher_moves
              : (Array.isArray(rationale?.teacher_moves) ? rationale.teacher_moves : []);
            const teacherMoves = teacherMovesRaw
              .filter((move: any) => move && typeof move === 'object')
              .slice(0, 3);
            const title =
              s?.title
              || s?.summary
              || recommendation?.title
              || recommendation?.summary
              || recommendation?.teacher_prompt_md
              || 'Suggestion';
            return (
              <div key={s.id} className="w-full p-2 border border-gray-200 rounded">
                <div className="flex w-full flex-col gap-2">
                  <div className="min-w-0 w-full">
                    <div className="text-sm font-semibold truncate">
                      {String(s?.suggestion_type ?? 'suggestion').toUpperCase()} • {targets} student{targets === 1 ? '' : 's'}
                      {mins !== null ? <span className="ml-2 text-xs font-normal text-gray-500">expires ~{mins}m</span> : null}
                    </div>
                    {statusText ? (
                      <span className="mt-1 inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700 uppercase">
                        {statusText}
                      </span>
                    ) : null}
                    <div className="mt-1 text-sm text-gray-700 whitespace-normal break-words">{title}</div>
                    {reasonSummary ? (
                      <div className="mt-1 text-xs text-gray-500">
                        Signals: {reasonSummary}
                      </div>
                    ) : null}
                    {teacherMoves.length > 0 ? (
                      <div className="mt-2 space-y-2 border-l-2 border-amber-200 pl-2">
                        <div className="text-xs uppercase tracking-wide text-gray-500">Teacher moves</div>
                        {teacherMoves.map((move: any, idx: number) => {
                          const label = String(move?.label || move?.reason_code || `Move ${idx + 1}`).trim();
                          const script = String(move?.script || move?.teacher_move || '').trim();
                          const check = String(move?.check_for_understanding || '').trim();
                          return (
                            <div key={`${suggestionId}-move-${idx}`} className="text-xs text-gray-700">
                              <div className="font-medium">{label}</div>
                              {script ? <div>{script}</div> : null}
                              {check ? <div className="text-gray-500">Check: {check}</div> : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                    {targetIds.length > 0 && (
                      <button
                        type="button"
                        className="mt-1 text-xs text-gray-500 hover:text-gray-700 underline"
                        onClick={() => onToggleAdaptiveTargets(suggestionId)}
                      >
                        {previewOpen ? 'Hide targets' : `Preview targets (${targetIds.length})`}
                      </button>
                    )}
                    {previewOpen && targetIds.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {previewTargetIds.map((studentId: number) => (
                          <span key={`${suggestionId}-target-${studentId}`} className="text-xs rounded-full bg-gray-100 px-2 py-1">
                            {rosterNameById.get(studentId) || `ID ${studentId}`}
                          </span>
                        ))}
                        {remainingTargetCount > 0 && (
                          <span className="text-xs text-gray-500 px-1 py-1">+{remainingTargetCount} more</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex w-full flex-wrap gap-2">
                    {isPending && (
                      <>
                        <button
                          type="button"
                          className="px-3 py-1 text-sm bg-green-600 text-white rounded disabled:opacity-60"
                          onClick={() => onApproveApplySuggestion(suggestionId, true)}
                          disabled={adaptiveLoading}
                        >
                          Apply
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-60"
                          onClick={() => onApproveApplySuggestion(suggestionId, false)}
                          disabled={adaptiveLoading}
                        >
                          Approve (no apply)
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-60"
                          onClick={() => onRejectSuggestion(suggestionId)}
                          disabled={adaptiveLoading}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {canApplyNow && (
                      <button
                        type="button"
                        className="px-3 py-1 text-sm bg-green-600 text-white rounded disabled:opacity-60"
                        onClick={() => onApplyApprovedSuggestion(suggestionId)}
                        disabled={adaptiveLoading}
                      >
                        Apply now
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {lastAdaptiveApplication && (
        <div className="mt-2 text-xs text-gray-600">
          Overlays written:{' '}
          <span className="font-semibold text-teal-700">Repair {lastAdaptiveApplication.repair}</span>
          {' • '}
          <span className="font-semibold text-indigo-700">Extend {lastAdaptiveApplication.extend}</span>
        </div>
      )}
    </div>
  </div>
  );
};
